// Mapbox GL JS utilities and configuration

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Get token from environment and set it
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';
mapboxgl.accessToken = MAPBOX_TOKEN;

/**
 * Check if Mapbox token is configured
 * @returns {boolean} True if token is set
 */
export const isMapboxConfigured = () => {
  const token = mapboxgl.accessToken || MAPBOX_TOKEN;
  // Check for valid token format (starts with pk., sk., or tk.)
  if (!token) return false;
  if (token === 'your_mapbox_token_here') return false;
  if (token.startsWith('pk.placeholder')) return false;
  // Valid tokens start with pk. (public), sk. (secret), or tk. (temporary)
  return token.startsWith('pk.') || token.startsWith('sk.') || token.startsWith('tk.');
};

/**
 * Initialize a new Mapbox map
 * @param {Object} options - Map options
 * @returns {Object} Mapbox map instance
 */
export const initializeMap = (options = {}) => {
  const {
    container,
    center = [28.7891, -31.5889], // [lng, lat] for Mthatha
    zoom = 12,
    style = 'mapbox://styles/mapbox/streets-v12',
  } = options;

  const map = new mapboxgl.Map({
    container,
    style,
    center,
    zoom,
    minZoom: 5,
    maxZoom: 18,
    attributionControl: true,
  });

  // Add navigation controls
  map.addControl(new mapboxgl.NavigationControl(), 'top-right');

  // Add scale control
  map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left');

  // Add fullscreen control
  map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

  return map;
};

/**
 * Fly to a location on the map
 * @param {Object} map - Mapbox map instance
 * @param {Array} center - Target center [lng, lat]
 * @param {number} zoom - Target zoom level
 * @param {number} duration - Animation duration in ms
 */
export const flyTo = (map, center, zoom = 14, duration = 1500) => {
  map.flyTo({
    center,
    zoom,
    duration,
    essential: true,
  });
};

/**
 * Fit the map to show all markers
 * @param {Object} map - Mapbox map instance
 * @param {Array} bounds - Array of [lng, lat] coordinates
 * @param {Object} options - Fit bounds options
 */
export const fitToBounds = (map, bounds, options = {}) => {
  const mapboxBounds = new mapboxgl.LngLatBounds();

  bounds.forEach((coord) => {
    if (Array.isArray(coord)) {
      mapboxBounds.extend(coord);
    } else if (coord.longitude && coord.latitude) {
      mapboxBounds.extend([coord.longitude, coord.latitude]);
    }
  });

  map.fitBounds(mapboxBounds, {
    padding: 50,
    maxZoom: 15,
    ...options,
  });
};

// Export mapboxgl for use in components
export { mapboxgl };
